// Pure privacy rules for PostHog, kept free of imports so the Node test suite
// can load this file alone. See docs/proposals/202610290101-posthog-product-analytics-design.md.

// Query parameters that identify a payment or could carry a credential. Every
// other parameter (utm_*, fbclid, gclid, ref, unexpected ones) is kept.
const DENIED_PARAMS = new Set(["stripe_checkout_session_id", "analytics_transaction_id"])
const DENIED_PARAM_PARTS = /token|session|code|email|password/i

// URL-valued properties posthog-js sets on events and on the person.
const URL_PROPERTIES = [
  "$current_url",
  "$referrer",
  "$initial_current_url",
  "$initial_referrer",
  "$prev_pageview_url",
  "$session_entry_url",
  "$session_entry_referrer",
]
const PATH_PROPERTIES = ["$pathname", "$initial_pathname", "$prev_pageview_pathname", "$session_entry_pathname"]

// Recordings hide every text node on signed-in pages (emails, chart names,
// birth details, readings). Public elements holding personal data carry the
// `ph-no-capture` class instead, which leaves them out entirely.
export const RECORDING_MASK_TEXT_SELECTOR = "html[data-ph-signed-in] *"

export type MaskPath = (url: string) => string

function deniedParam(name: string) {
  return DENIED_PARAMS.has(name.toLowerCase()) || DENIED_PARAM_PARTS.test(name)
}

// Same path masking as GA (chart and message IDs become :chart_id etc.), but
// the query string is kept minus the deny-list, and the fragment is dropped.
export function cleanPosthogUrl(url: string, maskPath: MaskPath): string {
  let parsed: URL
  try {
    parsed = new URL(url, "https://superbazi.me")
  } catch {
    return url
  }

  const query = new URLSearchParams()
  parsed.searchParams.forEach((value, name) => {
    if (!deniedParam(name)) query.append(name, value)
  })
  const search = query.toString()

  return `${parsed.origin}${maskPath(parsed.pathname)}${search ? `?${search}` : ""}`
}

type PropertyBag = Record<string, unknown>

function cleanBag(bag: PropertyBag | undefined, maskPath: MaskPath, siteOrigin: string) {
  if (!bag) return
  for (const key of URL_PROPERTIES) {
    const value = bag[key]
    if (typeof value !== "string" || value === "$direct") continue
    // Only same-site URLs carry our paths; external referrers are kept as-is.
    if (key.includes("referrer") && !value.startsWith(siteOrigin)) continue
    bag[key] = cleanPosthogUrl(value, maskPath)
  }
  for (const key of PATH_PROPERTIES) {
    const value = bag[key]
    if (typeof value === "string") bag[key] = maskPath(value)
  }
}

// Web vitals nest a copy of the page URL per metric, and heatmaps key their
// data by the raw page URL.
function cleanNested(properties: PropertyBag, maskPath: MaskPath, siteOrigin: string) {
  for (const [key, value] of Object.entries(properties)) {
    if (key.startsWith("$web_vitals_") && value && typeof value === "object") cleanBag(value as PropertyBag, maskPath, siteOrigin)
  }
  const heatmap = properties.$heatmap_data
  if (heatmap && typeof heatmap === "object") {
    properties.$heatmap_data = Object.fromEntries(
      Object.entries(heatmap as PropertyBag).map(([url, data]) => [cleanPosthogUrl(url, maskPath), data]),
    )
  }
}

// posthog-js before_send hook: rewrites every URL and path on the event and on
// the person ($set / $set_once). Returns the same event object.
export function cleanPosthogEvent<T extends { properties?: PropertyBag; $set?: PropertyBag; $set_once?: PropertyBag }>(
  event: T | null,
  maskPath: MaskPath,
  siteOrigin: string,
): T | null {
  if (!event) return event
  const properties = event.properties
  cleanBag(properties, maskPath, siteOrigin)
  if (properties) cleanNested(properties, maskPath, siteOrigin)
  cleanBag(properties?.$set as PropertyBag | undefined, maskPath, siteOrigin)
  cleanBag(properties?.$set_once as PropertyBag | undefined, maskPath, siteOrigin)
  cleanBag(event.$set, maskPath, siteOrigin)
  cleanBag(event.$set_once, maskPath, siteOrigin)
  return event
}

// What to do with PostHog identity for the page just rendered.
//   identify — a signed-in page that carries the user's id, not yet identified as them
//   reset    — a signed-out page after an identified session (logout)
export function identityAction(
  current: { identifiedId: string | null },
  page: { userId: string | null; signedIn: boolean },
): "identify" | "reset" | null {
  if (page.userId && page.userId !== current.identifiedId) return "identify"
  if (!page.signedIn && current.identifiedId) return "reset"
  return null
}
